import { FCC, useAnalytics, useTrackPageView } from "@dgs/core";
import { differenceInSeconds } from "date-fns";
import { createContext, useCallback, useContext, useRef } from "react";
import { useConfig } from "../shared/hooks/useConfig";
import { AdobeAnalytics } from "./AdobeAnalytics";
import {
	AllAnalyticsEvents,
	RegistrationAbortedAnalyticsEvent,
	RegistrationCompletedAnalyticsEvent,
	RegistrationFailedAnalyticsEvent,
	RegistrationStartedAnalyticsEvent,
	RegistrationStepAbortedAnalyticsEvent,
	RegistrationStepCompletedAnalyticsEvent,
	RegistrationStepStartedAnalyticsEvent,
	RegistrationSummaryAbortedAnalyticsEvent,
	RegistrationSummaryCompletedAnalyticsEvent,
	RegistrationSummaryStartedAnalyticsEvent,
} from "./analyticsEvents";
import { DotnetAnalytics } from "./DotnetAnalytics";

interface PMAnalyticsState {
	trackRegistrationStarted: (registrationId: string, currentStepId: number) => void;
	trackRegistrationCompleted: (registrationId: string) => void;
	trackRegistrationFailed: (registrationId: string) => void;
	trackRegistrationAborted: (registrationId: string, currentStepId: number) => void;
	trackNextStep: (registrationId: string, currentStepId: number, nextStepId: number) => void;
	trackPreviousStep: (registrationId: string, currentStepId: number, nextStepId: number) => void;

	trackSummaryStarted: (registrationId: string, currentStepId: number) => void;
	trackSummaryAborted: (registrationId: string, previousStepId: number) => void;
}

const payloadMapper = (e: AllAnalyticsEvents) => e.payload;

const PMAnalyticsContext = createContext<PMAnalyticsState | undefined>(undefined);

export const PMAnalyticsProvider: FCC = ({ children }) => {
	const registrationStartedRef = useRef<Date | null>(null);
	const stepStartedRef = useRef<Date | null>(null);
	const { analyticsUrl, productData } = useConfig();
	const { track } = useAnalytics();

	const trackRegistrationStarted = useCallback(
		(registrationId: string, stepId: number) => {
			if (registrationStartedRef.current === null) {
				const now = new Date();
				registrationStartedRef.current = now;
				stepStartedRef.current = now;
				track(new RegistrationStartedAnalyticsEvent(registrationId, stepId, now));
				track(new RegistrationStepStartedAnalyticsEvent(registrationId, stepId, now));
			}
		},
		[track],
	);

	const trackRegistrationCompleted = useCallback(
		(registrationId: string) => {
			if (registrationStartedRef.current !== null) {
				const now = new Date();
				track(
					new RegistrationCompletedAnalyticsEvent(
						registrationId,
						now,
						differenceInSeconds(now, registrationStartedRef.current),
					),
				);
				registrationStartedRef.current = null;

				if (stepStartedRef.current) {
					track(
						new RegistrationSummaryCompletedAnalyticsEvent(
							registrationId,
							now,
							differenceInSeconds(now, stepStartedRef.current),
						),
					);
					stepStartedRef.current = null;
				}
			}
		},
		[track],
	);

	const trackRegistrationFailed = useCallback(
		(registrationId: string) => {
			const now = new Date();
			track(new RegistrationFailedAnalyticsEvent(registrationId, now));
		},
		[track],
	);

	const trackRegistrationAborted = useCallback(
		(registrationId: string, currentStepId: number) => {
			if (registrationStartedRef.current !== null) {
				const now = new Date();
				track(
					new RegistrationAbortedAnalyticsEvent(
						registrationId,
						now,
						differenceInSeconds(now, registrationStartedRef.current),
					),
				);
				registrationStartedRef.current = null;

				if (stepStartedRef.current) {
					track(
						new RegistrationStepAbortedAnalyticsEvent(
							currentStepId,
							registrationId,
							now,
							differenceInSeconds(now, stepStartedRef.current),
						),
					);
					stepStartedRef.current = null;
				}
			}
		},
		[track],
	);

	const trackNextStep = useCallback(
		(registrationId: string, currentStepId: number, nextStepId: number) => {
			const now = new Date();
			if (stepStartedRef.current) {
				track(
					new RegistrationStepCompletedAnalyticsEvent(
						currentStepId,
						registrationId,
						now,
						differenceInSeconds(now, stepStartedRef.current),
					),
				);
			}
			stepStartedRef.current = now;
			track(new RegistrationStepStartedAnalyticsEvent(registrationId, nextStepId, now));
		},
		[track],
	);

	const trackPreviousStep = useCallback(
		(registrationId: string, currentStepId: number, previousStepId: number) => {
			const now = new Date();
			if (stepStartedRef.current) {
				track(
					new RegistrationStepAbortedAnalyticsEvent(
						registrationId,
						currentStepId,
						now,
						differenceInSeconds(now, stepStartedRef.current),
					),
				);
			}
			stepStartedRef.current = now;
			track(new RegistrationStepStartedAnalyticsEvent(registrationId, previousStepId, now));
		},
		[track],
	);

	const trackSummaryStarted = useCallback(
		(registrationId: string, currentStepId: number) => {
			const now = new Date();
			if (stepStartedRef.current) {
				track(
					new RegistrationStepCompletedAnalyticsEvent(
						registrationId,
						currentStepId,
						now,
						differenceInSeconds(now, stepStartedRef.current),
					),
				);
			}
			stepStartedRef.current = now;
			track(new RegistrationSummaryStartedAnalyticsEvent(registrationId, now));
		},
		[track],
	);

	const trackSummaryAborted = useCallback(
		(registrationId: string, previousStepId: number) => {
			const now = new Date();
			if (stepStartedRef.current) {
				track(
					new RegistrationSummaryAbortedAnalyticsEvent(
						registrationId,
						now,
						differenceInSeconds(now, stepStartedRef.current),
					),
				);
			}
			stepStartedRef.current = now;
			track(new RegistrationStepStartedAnalyticsEvent(registrationId, previousStepId, now));
		},
		[track],
	);

	return (
		<PMAnalyticsContext.Provider
			value={{
				trackRegistrationStarted,
				trackRegistrationAborted,
				trackNextStep,
				trackPreviousStep,
				trackRegistrationCompleted,
				trackRegistrationFailed,
				trackSummaryAborted,
				trackSummaryStarted,
			}}
		>
			{productData.enableAnalytics ? (
				<DotnetAnalytics socketUrl={analyticsUrl} payloadMapper={payloadMapper}>
					{productData.enableAdobeAnalytics ? (
						<AdobeAnalytics>
							<>
								<TrackPageViews />
								{children}
							</>
						</AdobeAnalytics>
					) : (
						<>
							<TrackPageViews />
							{children}
						</>
					)}
				</DotnetAnalytics>
			) : (
				children
			)}
		</PMAnalyticsContext.Provider>
	);
};

export const usePMAnalytics = () => {
	const ctx = useContext(PMAnalyticsContext);

	if (ctx === undefined) {
		throw new Error("PMAnalyticsContext undefined. Did you forget a provider?");
	}

	return ctx;
};

const TrackPageViews = () => {
	useTrackPageView();
	return null;
};
